import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spin, Empty, Button, Space, Modal, Popconfirm, Typography, theme, Tooltip } from 'antd';
import { EditOutlined, PrinterOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { HiDocumentText } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import FormResponseView from '../../clinicalForms/FormResponseView.component';
import Printer from '../../printer/Printer.component';
import { transformFormSubmissionForPrinter } from '../../../utils/formDataTransformer';
import { mapFormResponseDtoToEntity } from '../../../utils/functions';

const { Title } = Typography;

const FormBasedEntityView = ({
    entityId,
    getDetails,
    getFormDetails,
    getFormResponse,
    entityStateKey,
    entityKey,
    entityLabel,
    onEdit,
    onDelete,
    patientFullName,
    patientDateOfBirth,
    patientYearOfBirth,
    patientId,
    /** When true, entityId is FormResponse id; getDetails should be getFormResponseDetails; skip redundant form-response fetch */
    formResponseAsEntity = false,
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const [printerModalVisible, setPrinterModalVisible] = useState(false);
    const [printData, setPrintData] = useState(null);
    // Local caches to prevent shared Redux state overwrites from sibling/child components
    const [cachedFormDetails, setCachedFormDetails] = useState(null);
    const [cachedFormResponse, setCachedFormResponse] = useState(null);

    const entityDetails = useSelector(state => state[entityStateKey]);
    const { loading: loadingDetails, [entityKey]: rawEntity } = entityDetails || {};

    const entity = React.useMemo(() => {
        if (!formResponseAsEntity) return rawEntity;
        return mapFormResponseDtoToEntity(rawEntity) || rawEntity;
    }, [formResponseAsEntity, rawEntity]);
    
    const formDetails = useSelector(state => state.formDetails);
    const { form: selectedFormDetails, loading: loadingFormDetails } = formDetails || {};
    
    const formResponseDetails = useSelector(state => state.formResponseDetails);
    const { formResponse: existingFormResponse, loading: loadingFormResponse } = formResponseDetails || {};

    // Clear caches when entity changes
    useEffect(() => {
        setCachedFormDetails(null);
        setCachedFormResponse(null);
    }, [entityId]);

    useEffect(() => {
        if (entityId) {
            const id = typeof entityId === 'string' || typeof entityId === 'number' 
                ? String(entityId) 
                : (entityId?.id ? String(entityId.id) : null);
            if (id) {
                dispatch(getDetails(id));
            }
        }
    }, [entityId, dispatch, getDetails]);

    useEffect(() => {
        if (entity?.defaultFormId && (!cachedFormDetails || cachedFormDetails.id !== entity.defaultFormId)) {
            dispatch(getFormDetails(entity.defaultFormId));
        }
    }, [entity?.defaultFormId, cachedFormDetails, dispatch, getFormDetails]);

    useEffect(() => {
        if (formResponseAsEntity) return;
        if (entity?.formResponseId && (!cachedFormResponse || cachedFormResponse.id !== entity.formResponseId)) {
            dispatch(getFormResponse(entity.formResponseId));
        }
    }, [entity?.formResponseId, cachedFormResponse, dispatch, getFormResponse, formResponseAsEntity]);

    // Cache when Redux state matches what this entity needs
    useEffect(() => {
        if (selectedFormDetails && entity?.defaultFormId && selectedFormDetails.id === entity.defaultFormId) {
            setCachedFormDetails(selectedFormDetails);
        }
    }, [selectedFormDetails, entity?.defaultFormId]);

    useEffect(() => {
        if (existingFormResponse && entity?.formResponseId && existingFormResponse.id === entity.formResponseId) {
            setCachedFormResponse(existingFormResponse);
        }
    }, [existingFormResponse, entity?.formResponseId]);

    // Cleanup shared state on unmount
    useEffect(() => {
        return () => {
            dispatch({ type: 'FORM_DETAILS_RESET' });
        };
    }, [dispatch]);
    
    // Use cached data if available, otherwise fall back to Redux state
    const effectiveFormDetails = cachedFormDetails || selectedFormDetails;
    const effectiveFormResponse = cachedFormResponse || existingFormResponse;

    // Prefer the schema the patient actually answered against (frozen on the response at submission
    // time), like traditional EHR systems. Only fall back to the live form definition for legacy
    // responses submitted before schema snapshots existed, so editing a form later can't reshape
    // how older answers are displayed.
    const usingSchemaSnapshot = !!effectiveFormResponse?.schemaSnapshot;
    const formSchema = React.useMemo(() => {
        const schemaSource = effectiveFormResponse?.schemaSnapshot || effectiveFormDetails?.schemaJson;
        if (!schemaSource) return null;
        try {
            return typeof schemaSource === 'string' ? JSON.parse(schemaSource) : schemaSource;
        } catch (error) {
            console.error('Error parsing form schema:', error);
            return null;
        }
    }, [effectiveFormResponse?.schemaSnapshot, effectiveFormDetails?.schemaJson]);

    const formResponseData = React.useMemo(() => {
        const responseField = effectiveFormResponse?.responseData || effectiveFormResponse?.responseJson;
        if (!responseField) return null;
        try {
            return typeof responseField === 'string'
                ? JSON.parse(responseField)
                : responseField;
        } catch (error) {
            console.error('Error parsing form response data:', error);
            return null;
        }
    }, [effectiveFormResponse]);

    // Only show loading if we don't have cached versions
    const isLoading = loadingDetails 
        || (!cachedFormDetails && loadingFormDetails) 
        || (!formResponseAsEntity && !cachedFormResponse && loadingFormResponse);
    
    const preparedPrintData = useMemo(() => {
        if (!formSchema || !formResponseData || !entity) return null;

        const transformedData = transformFormSubmissionForPrinter(formSchema, formResponseData);

        const submittedAtIso = entity.updatedAt ? String(entity.updatedAt) : '';
        const submittedByStr = entity.updatedBy || '';
        const formTitle = effectiveFormDetails?.name || effectiveFormResponse?.formName || 'Form Submission';

        return {
            formName: formTitle,
            header: {
                type: 'bloc',
                content: { formName: formTitle },
            },
            patientInfo: {
                type: 'bloc',
                content: {
                    patientName: patientFullName || '',
                    patientId: patientId || '',
                    dateOfBirth: patientDateOfBirth || '',
                    submittedDate: submittedAtIso,
                    submittedBy: submittedByStr,
                },
            },
            footer: {
                type: 'bloc',
                content: {
                    submittedDate: submittedAtIso,
                    submittedBy: submittedByStr,
                },
            },
            submittedDate: submittedAtIso,
            submittedBy: submittedByStr,
            sections: transformedData?.sections?.content?.items || [],
        };
    }, [formSchema, formResponseData, entity, effectiveFormDetails, effectiveFormResponse, patientFullName, patientId, patientDateOfBirth]);
    
    const handlePrint = () => {
        if (preparedPrintData) {
            setPrintData(preparedPrintData);
            setPrinterModalVisible(true);
        }
    };

    const handleDownloadJson = () => {
        // Export the submitted answers only — not the form definition/schema.
        const answers = formResponseData
            || (() => {
                const raw = effectiveFormResponse?.responseJson || entity?.responseJson;
                if (!raw) return null;
                try {
                    return typeof raw === 'string' ? JSON.parse(raw) : raw;
                } catch {
                    return null;
                }
            })();
        if (!answers) return;

        const payload = {
            id: entity?.formResponseId || entity?.id,
            formName: effectiveFormDetails?.name || effectiveFormResponse?.formName || entityLabel,
            formCode: effectiveFormDetails?.code || effectiveFormResponse?.formCode,
            formVersion: effectiveFormResponse?.formVersion || entity?.formVersion,
            status: effectiveFormResponse?.status || entity?.status,
            submittedAt: entity?.updatedAt,
            submittedBy: entity?.updatedBy,
            answers,
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${(effectiveFormDetails?.code || effectiveFormResponse?.formCode || 'form-response')}-${entity?.formResponseId || entity?.id}.json`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                minHeight: '200px'
            }}>
                <Spin size="large" tip={t(`Loading ${entityLabel.toLowerCase()}...`)} />
            </div>
        );
    }

    if (!entity) {
        return <Empty description={t(`${entityLabel} not found`)} style={{ marginTop: '60px' }} />;
    }

    // Show action buttons even if form is incomplete.
    // Guard: when relying on the live form (no snapshot yet), ensure loaded form details match the
    // entity's form to prevent stale data from other entity types. A cached snapshot is self-contained
    // and doesn't need the live Form to have loaded (or even still exist unchanged).
    const formDetailsReady = usingSchemaSnapshot || effectiveFormDetails?.id === entity.defaultFormId;
    const hasFormData = entity.defaultFormId && entity.formResponseId && formSchema && formResponseData && formDetailsReady;
    const formTitle = (effectiveFormDetails?.id === entity?.defaultFormId ? effectiveFormDetails?.name : null)
        || effectiveFormResponse?.formName
        || entityLabel;

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {hasFormData && (
                    <Space>
                        <HiDocumentText style={{ fontSize: 24, color: token.colorPrimary }} />
                        <Title level={4} style={{ margin: 0 }}>
                            {formTitle}
                        </Title>
                    </Space>
                )}
                <Space>
                    {hasFormData && (
                        <Button
                            size="small"
                            icon={<PrinterOutlined />}
                            onClick={handlePrint}
                        >
                            {t('Print')}
                        </Button>
                    )}
                    {hasFormData && (
                        <Tooltip title={t('Download response as JSON')}>
                            <Button
                                size="small"
                                type="text"
                                icon={<DownloadOutlined style={{ color: token.colorTextTertiary }} />}
                                onClick={handleDownloadJson}
                                aria-label={t('Download JSON')}
                            />
                        </Tooltip>
                    )}
                    {onEdit && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={onEdit}
                        >
                            {t('Edit')}
                        </Button>
                    )}
                    {onDelete && (
                        <Popconfirm
                            title={t('Delete item?')}
                            description={t('This action cannot be undone.')}
                            onConfirm={onDelete}
                            okText={t('Delete')}
                            cancelText={t('Cancel')}
                            okButtonProps={{ danger: true }}
                        >
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            >
                                {t('Delete')}
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            </div>

            <div style={{ flex: 1, overflow: 'auto' }}>
                {hasFormData ? (
                    <FormResponseView
                        formSchema={formSchema}
                        formValues={formResponseData}
                        patientFullName={patientFullName}
                        patientDateOfBirth={patientDateOfBirth}
                        patientYearOfBirth={patientYearOfBirth}
                        patientId={patientId}
                    />
                ) : (
                    <Empty 
                        description={
                            !entity.defaultFormId 
                                ? t('No form template associated. Click Edit to add one.')
                                : !entity.formResponseId
                                ? t('No form response data available. Click Edit to add data.')
                                : t('Form data could not be loaded.')
                        } 
                        style={{ marginTop: '60px' }}
                    />
                )}
            </div>
            
            <Modal
                title={t(`Print ${entityLabel}`)}
                open={printerModalVisible}
                onCancel={() => {
                    setPrinterModalVisible(false);
                    setPrintData(null);
                }}
                footer={null}
                width="85%"
                destroyOnClose
                styles={{ body: { padding: 0 } }}
            >
                {printData ? (
                    <Printer
                        embed
                        autoGenerate
                        configEndpoint="/print/configs/form_submission"
                        generateEndpoint="/print/documents/generate"
                        data={printData}
                    />
                ) : (
                    <Empty description={t('No data available for printing')} style={{ margin: 16 }} />
                )}
            </Modal>
        </div>
    );
};

export default FormBasedEntityView;
